import { useState } from "react";
import ModalDefault from "../../ModalDefault";
import { comum } from "../../../styles/Themes/light";
import { IconClose } from "../../icons";
import { ButtonCloseModal, ModalForm, ModalFormScroll, ModalTitle, ModalContent, ModalButtonCancel, ModalButtonPrimary, ButtonRow } from "./styles";
import InputDefault from "../../form/InputDefault";
import InputMoney from "../../form/InputMoney";
import api from "../../../services/api";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from "../../../config/toastConfig";
import { useHistory } from "react-router-dom";

interface ModalNovoPlanoProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function ModalNovoPlano({ open, onClose, onSuccess }: ModalNovoPlanoProps) {
    const history = useHistory();
    const [nome, setNome] = useState("");
    const [quantidadeCreditos, setQuantidadeCreditos] = useState("");
    const [valorPlano, setValorPlano] = useState("0.00");
    const [valorOnboarding, setValorOnboarding] = useState("0.00");
    const [validadeDias, setValidadeDias] = useState("30");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    function validate(): boolean {
        const e: Record<string, string> = {};
        if (!nome || !nome.trim()) e.nome = "Informe o nome do plano";
        else if (nome.length > 50) e.nome = "Nome do plano deve ter no máximo 50 caracteres";
        const cred = Number(quantidadeCreditos);
        if (!quantidadeCreditos || isNaN(cred) || cred <= 0) e.quantidade_creditos = "Informe uma quantidade válida de créditos";
        const vp = parseFloat(valorPlano);
        if (valorPlano === "" || isNaN(vp) || vp < 0) e.valor_plano = "Informe um valor válido para o plano";
        else if (vp === 0) e.valor_plano = "O valor do plano deve ser maior que zero";
        const vo = parseFloat(valorOnboarding);
        if (valorOnboarding !== "" && valorOnboarding !== "0.00" && (isNaN(vo) || vo < 0)) e.valor_onboarding = "Valor onboarding não pode ser negativo";
        const vd = Number(validadeDias);
        if (validadeDias !== "" && (isNaN(vd) || vd < 1)) e.validade = "Validade deve ser um número positivo";
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function handleCriar() {
        if (!validate() || submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                nome: nome.trim().substring(0, 50),
                quantidade_creditos: Number(quantidadeCreditos),
                valor_plano: parseFloat(valorPlano),
                valor_onboarding: valorOnboarding === "" || valorOnboarding === "0.00" ? undefined : parseFloat(valorOnboarding),
                validade_creditos_dias: validadeDias ? Number(validadeDias) : 30,
            };
            const res = await api.post("/plano-solicitante-template", payload);
            if (res.data?.status !== "success") {
                throw new Error(res.data?.message || "Erro ao criar plano");
            }
            const id = res.data?.result?.id_plano_solicitante_template;
            toast.success("Plano criado com sucesso!", toastDefs);
            onClose();
            setNome("");
            setQuantidadeCreditos("");
            setValorPlano("0.00");
            setValorOnboarding("0.00");
            setValidadeDias("30");
            setErrors({});
            onSuccess?.();
            window.dispatchEvent(new Event("refreshCounters"));
            if (id) history.push(`/planos/${id}`);
        } catch (err: any) {
            toast.error(err.response?.data?.message || err.message || "Erro ao criar plano", toastDefsError);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <ModalDefault id="modalNovoPlano" onClose={onClose} openModal={open} width="100%" maxWidth="480px" noScroll contentPadding="20px">
            <ModalContent>
                <ButtonCloseModal onClick={onClose} type="button" aria-label="Fechar">
                    <IconClose color={comum.colors.interfaceGray300} />
                </ButtonCloseModal>
                <ModalTitle>
                    <h1>Novo Plano</h1>
                    <p>Preencha as informações abaixo para criar um novo plano.</p>
                </ModalTitle>
                <ModalFormScroll>
                    <ModalForm>
                        <div className="input-full">
                            <InputDefault
                                title="Nome do plano"
                                obrigatorio
                                value={nome}
                                onChangeText={setNome}
                                placeholder="Ex: Enterprise"
                                maxLength={50}
                                className={errors.nome ? "error" : ""}
                            />
                            {errors.nome && <span className="error-msg">{errors.nome}</span>}
                        </div>
                        <div className="input-full">
                            <InputDefault
                                title="Quantidade de créditos"
                                obrigatorio
                                type="number"
                                value={quantidadeCreditos}
                                onChangeText={setQuantidadeCreditos}
                                placeholder="Ex: 100"
                                min="1"
                                className={errors.quantidade_creditos ? "error" : ""}
                            />
                            {errors.quantidade_creditos && <span className="error-msg">{errors.quantidade_creditos}</span>}
                        </div>
                        <div className="input-full">
                            <InputMoney
                                title="Valor do plano"
                                obrigatorio
                                value={valorPlano}
                                onChangeText={setValorPlano}
                                placeholder="R$ 0,00"
                                className={errors.valor_plano ? "error" : ""}
                            />
                            {errors.valor_plano && <span className="error-msg">{errors.valor_plano}</span>}
                        </div>
                        <div className="input-full">
                            <InputMoney
                                title="Valor onboarding"
                                value={valorOnboarding}
                                onChangeText={setValorOnboarding}
                                placeholder="R$ 0,00"
                                className={errors.valor_onboarding ? "error" : ""}
                            />
                            {errors.valor_onboarding && <span className="error-msg">{errors.valor_onboarding}</span>}
                        </div>
                        <div className="input-full">
                            <InputDefault
                                title="Validade dos créditos (dias)"
                                value={validadeDias}
                                onChangeText={setValidadeDias}
                                placeholder="30"
                                type="number"
                                min="1"
                                className={errors.validade ? "error" : ""}
                            />
                            {errors.validade && <span className="error-msg">{errors.validade}</span>}
                        </div>
                        <ButtonRow>
                            <ModalButtonCancel type="button" onClick={onClose}>Cancelar</ModalButtonCancel>
                            <ModalButtonPrimary type="button" onClick={handleCriar} disabled={submitting}>
                                {submitting ? "Criando..." : "Criar plano"}
                            </ModalButtonPrimary>
                        </ButtonRow>
                    </ModalForm>
                </ModalFormScroll>
            </ModalContent>
        </ModalDefault>
    );
}
